/* eslint-disable */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";

import "../../App.css";
import VisaImage from "../../assets/images/visa.png";
import VerveImage from "../../assets/images/verve.png";
import useHttp from "../../hooks/use-http";
import CardContext from "../../store/card-context";
import { useTitle } from "../../hooks/useTitle";
import toast from "react-hot-toast";
import Spinner from "../Spinner";
import { usePaymentSession } from "./usePaymentSession";

const CardPayment = () => {
  const { transactionInfo, merchantId } = useOutletContext();
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardType, setCardType] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [paymentSource, setPaymentSource] = useState("new");
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedPaymentId, setSelectedSavedPaymentId] = useState(null);

  // Sandbox simulation states
  const [simulationType, setSimulationType] = useState("visaApproved");
  const [testCards, setTestCards] = useState();

  const {
    isLoading: transactionLoading,
    sendRequest: CardPayment,
  } = useHttp();
  const { isLoading: savedCardsLoading, sendRequest: fetchSavedCards } =
    useHttp();
  const { sendRequest: MerchantInfo } = useHttp();
  const navigate = useNavigate();
  const { setCardDetails } = useContext(CardContext);
  const [loading, setLoading] = useState(false);

  const { id } = useParams();
  useTitle("Card");

  const cardNumberRef = useRef(null);
  const expiryDateRef = useRef(null);
  const cvvRef = useRef(null);

  const { markPaymentSubmitted } = usePaymentSession(id);

  // Fetch Merchant Info to get testCards for sandbox
  const getMerchantInfo = useCallback(
    (data) => {
      setTestCards(data?.data?.testCards);
    },
    [setTestCards]
  );

  const fetchMerchantInfo = useCallback(async () => {
    if (!id) return;
    MerchantInfo(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/sandbox/checkout/${id}`,
        headers: {
          "ngrok-skip-browser-warning": "true",
          "Content-Type": "application/json",
        },
      },
      getMerchantInfo
    );
  }, [MerchantInfo, getMerchantInfo, id]);

  useEffect(() => {
    fetchMerchantInfo();
  }, [fetchMerchantInfo]);

  // Set card inputs automatically based on selected test simulation card
  useEffect(() => {
    if (paymentSource === "new") {
      let num = "";
      let type = "visa";
      if (simulationType === "visaApproved") {
        num = testCards?.visaApproved || "4999 0821 0002 9373";
        type = "visa";
      } else if (simulationType === "verveApproved") {
        num = testCards?.verveApproved || "5060 9905 8000 0217 499";
        type = "verve";
      } else if (simulationType === "visaDeclined") {
        num = testCards?.visaDeclined || "4187 4221 0017 3247";
        type = "visa";
      }
      setCardNumber(num);
      setCardType(type);
      setExpiryDate("12/28");
      setCvv("123");
    }
  }, [simulationType, testCards, paymentSource]);

  const getSavedCards = useCallback(
    (data) => {
      setSavedCards(Array.isArray(data?.cards) ? data.cards : []);
    },
    [setSavedCards],
  );

  useEffect(() => {
    const email = transactionInfo?.data?.email || transactionInfo?.data?.customerEmail;
    if (!merchantId || !email) return;

    fetchSavedCards(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/merchants/${merchantId}/cards/by-email?email=${encodeURIComponent(
          email,
        )}`,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
      getSavedCards,
    );
  }, [
    fetchSavedCards,
    getSavedCards,
    merchantId,
    transactionInfo?.data?.email,
    transactionInfo?.data?.customerEmail,
  ]);

  const isTestMode =
    transactionInfo?.data &&
    (transactionInfo.data.isLive === false || transactionInfo.data.isLive === "false");

  const types = {
    visa: /^4/,
    verve: /(^506|^507|^650)[0-9]{0,16}/,
    mastercard: /^5[1-5]/,
    amex: /^3[47]/,
    unionpay: /^62/,
    afrigo: /^5640/,
    payattitudecard: /^9800/,
  };

  const handleCardNumberChange = (event) => {
    let inputValue = event.target.value.replace(/\D/g, "");

    let detectedCardType = "";
    for (const [type, pattern] of Object.entries(types)) {
      if (pattern.test(inputValue)) {
        detectedCardType = type;
        break;
      }
    }

    setCardType(detectedCardType);

    inputValue = inputValue
      .replace(/\s/g, "")
      .replace(/(\d{4})/g, "$1 ")
      .trim();

    const maxLengths = {
      amex: 18,
      unionpay: 23,
    };
    const maxLen = maxLengths[detectedCardType] || 19;

    if (inputValue.length <= maxLen) {
      setCardNumber(inputValue);
      if (inputValue.length === maxLen && expiryDateRef.current) {
        expiryDateRef.current.focus();
      }
    }
  };

  const handleExpiryDateChange = (event) => {
    let inputValue = event.target.value.replace(/\D/g, "");

    if (inputValue.length > 2) {
      const month = inputValue.substring(0, 2);
      const year = inputValue.substring(2, 4);
      inputValue = `${month}/${year}`;
    }

    setExpiryDate(inputValue);

    if (inputValue.length === 5 && cvvRef.current) {
      cvvRef.current.focus();
    }
  };

  const handleCvvChange = (event) => {
    const inputValue = event.target.value.replace(/\D/g, "");
    const maxLen = cardType === "amex" ? 4 : 3;
    if (inputValue.length <= maxLen) {
      setCvv(inputValue);
    }
  };

  const processCard = (data) => {
    setLoading(false);
    if (data) {
      markPaymentSubmitted();
      navigate(`/${id}/payment-confirmation/${id}/sandbox-pay`);
    } else {
      toast.error("Card transaction failed");
      navigate(`/sandbox-pay/card-failed/${id}`);
    }
  };

  const payTransaction = (event) => {
    event.preventDefault();

    if (paymentSource === "saved" && !selectedSavedPaymentId) {
      toast.error("Please select a saved card to proceed.");
      return;
    }

    setLoading(true);

    let transaction;

    if (paymentSource === "saved") {
      transaction = {
        id,
        paymentId: selectedSavedPaymentId,
        scheme: cardType,
      };
    } else {
      if (!cardType) {
        toast.error("Invalid or unsupported card type");
        setLoading(false);
        return;
      }
      if (!expiryDate || !/^\d{2}\/\d{2}$/.test(expiryDate)) {
        toast.error("Invalid expiry date (MM/YY format required)");
        setLoading(false);
        return;
      }
      const requiredCvvLength = cardType === "amex" ? 4 : 3;
      if (!cvv || cvv.length < requiredCvvLength) {
        toast.error(`CVV must be ${requiredCvvLength} digits for ${cardType.toUpperCase()}`);
        setLoading(false);
        return;
      }

      transaction = {
        id,
        scheme: cardType,
        cardNumber: cardNumber.replace(/\s/g, ""),
        expiryDate,
        cvv,
        saveCard,
        method: "card",
        cardHolderName: "John Doe",
      };
    }

    setCardDetails(transaction);

    CardPayment(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/sandbox/checkout/${id}/pay`,
        method: "POST",
        body: transaction,
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
        },
      },
      processCard
    ).catch(() => {
      setLoading(false);
    });
  };

  const currentCurrencySymbol = transactionInfo?.data?.currency === "NGN" ? "₦" : (transactionInfo?.data?.currency || "NGN");
  const totalAmountValue = (transactionInfo?.data?.amount || 0) + (transactionInfo?.data?.fee || 0);

  return (
    <div className="w-full max-w-[560px] mx-auto py-2 px-4 sm:px-0">
      {/* Test Transaction Ribbon */}
      {isTestMode && (
        <div
          id="test-transaction-ribbon"
          data-testid="test-transaction-ribbon"
          className="w-full mb-5"
        >
          <div className="w-full bg-gradient-to-r from-red-600 via-rose-600 to-red-600 text-white text-xs sm:text-sm font-bold py-2.5 px-4 rounded-lg shadow-sm border border-red-700/30 flex items-center justify-center gap-2 uppercase tracking-wider text-center">
            <svg
              className="w-4 h-4 flex-shrink-0 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span>TEST TRANSACTION</span>
          </div>
        </div>
      )}

      <h2 className="text-xl font-bold text-slate-800 text-center mb-6">
        Enter your card details to pay
      </h2>

      <form onSubmit={payTransaction} className="space-y-5">
        {/* Toggle between saved card and new card details */}
        {savedCards.length > 0 && (
          <div
            onClick={() => {
              if (paymentSource === "saved") {
                setPaymentSource("new");
                setSelectedSavedPaymentId(null);
              } else {
                setPaymentSource("saved");
                setSaveCard(false);
              }
            }}
            className="flex items-center gap-3 cursor-pointer py-3 border-b border-slate-100 mb-5 select-none"
          >
            <div className="relative flex items-center justify-center flex-shrink-0">
              <input
                type="checkbox"
                id="use_saved_card"
                checked={paymentSource === "saved"}
                onChange={() => { }}
                className="sr-only"
              />
              <div
                className={`w-[18px] h-[18px] rounded border flex items-center justify-center transition-colors ${paymentSource === "saved"
                    ? "bg-[#00aeef] border-[#00aeef]"
                    : "border-slate-300 bg-white"
                  }`}
              >
                {paymentSource === "saved" && (
                  <svg className="w-3.5 h-3.5 text-white pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm font-bold text-slate-700">
              Use saved Card
            </span>
          </div>
        )}

        {/* Saved Cards Selection List */}
        {paymentSource === "saved" && (
          <div className="bg-[#f0f9ff]/50 p-4 rounded-xl border border-[#d0ecfa] overflow-y-auto max-h-[200px] space-y-2 mb-5">
            {savedCardsLoading ? (
              <p className="text-xs text-slate-500">Loading saved cards...</p>
            ) : savedCards.length === 0 ? (
              <p className="text-xs text-slate-500">No saved cards found for this email.</p>
            ) : (
              savedCards.map((card) => (
                <label
                  key={card?.paymentId ?? card?.id}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer active:scale-[0.99] transition-all duration-200 ${selectedSavedPaymentId === card?.paymentId
                      ? "border-[#00aeef] bg-white shadow-sm"
                      : "border-slate-200 bg-white/70 hover:bg-white"
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="saved_card_choice"
                      checked={selectedSavedPaymentId === card?.paymentId}
                      onChange={() => {
                        setSelectedSavedPaymentId(card?.paymentId);
                        setCardType(card?.scheme?.toLowerCase() || "");
                      }}
                      className="w-4 h-4 text-[#00aeef] cursor-pointer"
                    />
                    <div>
                      <p className="text-xs font-bold text-slate-800">
                        {card?.scheme?.toUpperCase() || "CARD"} •••• {card?.maskedPAN || card?.pan || ""}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Expires {card?.expirationDate || card?.expiry || "--"}
                      </p>
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        )}

        {/* Sandbox Simulation Options */}
        {paymentSource === "new" && (
          <div className="bg-slate-50 p-4 border border-slate-200 rounded-lg">
            <span className="block text-[10px] font-extrabold text-slate-500 uppercase tracking-wider mb-3 text-center">
              Select a simulation flow
            </span>
            <div className="flex flex-col gap-2">
              {[
                { id: "visaApproved", label: "Simulate Success (Visa)", desc: "Visa approved payment outcome", badge: "Approved", color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
                { id: "verveApproved", label: "Simulate Success (Verve)", desc: "Verve approved payment outcome", badge: "Approved", color: "bg-emerald-50 text-emerald-700 border-emerald-100" },
                { id: "visaDeclined", label: "Simulate Decline", desc: "Visa declined payment outcome", badge: "Declined", color: "bg-rose-50 text-rose-700 border-rose-100" },
              ].map((opt) => (
                <div
                  key={opt.id}
                  onClick={() => setSimulationType(opt.id)}
                  className={`flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${simulationType === opt.id
                    ? "border-[#00aeef] bg-sky-50/40 shadow-sm"
                    : "border-slate-100 hover:border-slate-200 bg-white"
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${simulationType === opt.id ? "border-[#00aeef]" : "border-slate-300"
                      }`}>
                      {simulationType === opt.id && <div className="w-2 h-2 rounded-full bg-[#00aeef]\" />}
                    </div>
                    <div className="flex flex-col text-left">
                      <span className="text-xs font-bold text-slate-800">{opt.label}</span>
                      <span className="text-[10px] text-slate-400 font-mono mt-0.5">
                        {opt.id === "visaApproved" ? (testCards?.visaApproved || "4999 0821 0002 9373") :
                          opt.id === "verveApproved" ? (testCards?.verveApproved || "5060 9905 8000 0217 499") :
                            (testCards?.visaDeclined || "4187 4221 0017 3247")}
                      </span>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold border ${opt.color}`}>
                    {opt.badge}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && (
          <p className="text-center text-xs text-[#00aeef] animate-pulse">
            Processing payment, please do not refresh...
          </p>
        )}

        <button
          type="submit"
          className="w-full h-[54px] bg-[#00aeef] hover:bg-[#0096ce] active:scale-[0.99] text-white rounded-lg font-bold text-base cursor-pointer transition-all disabled:opacity-75 disabled:cursor-not-allowed flex items-center justify-center"
          disabled={loading || transactionLoading}
        >
          {loading || transactionLoading ? (
            <Spinner />
          ) : (
            `Pay ${currentCurrencySymbol}${totalAmountValue.toLocaleString()}`
          )}
        </button>

        <p className="text-center text-[11px] text-slate-400 font-medium flex items-center justify-center gap-1 mt-2">
          <svg width="10" height="10" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect y="5" width="14" height="11" rx="2" fill="#00aeef" />
            <path d="M3 5V4.5C3 2.567 4.567 1 6.5 1V1C8.433 1 10 2.567 10 4.5V5" stroke="#00aeef" strokeWidth="2" strokeLinecap="round" />
            <circle cx="7" cy="10.5" r="1.5" fill="white" />
          </svg> Your connection is encrypted and secure
        </p>

        <p className="text-center text-[12px] text-black leading-relaxed font-normal max-w-[460px] mx-auto">
          You'll be redirected to your bank's secure page to enter a One-Time Password (OTP). Check your SMS or email for the code and enter it to complete the transaction.
        </p>
      </form>
    </div>
  );
};

export default CardPayment;